// Typeset the maths marks on a question paper: stacked fractions, raised
// powers, a small log base, square roots, and Word matrices. The stored
// wording is unchanged. A Word matrix is written [■(x&6@5&15)]: & separates
// columns and @ separates rows.

function isAtomChar(ch) {
  return /[0-9A-Za-z.^√πθ]/.test(ch) || /[\u0370-\u03FF\u2070-\u209F]/.test(ch);
}

function matchingClose(text, open) {
  let depth = 0;
  for (let i = open; i < text.length; i += 1) {
    if (text[i] === '(') depth += 1;
    else if (text[i] === ')') {
      depth -= 1;
      if (depth === 0) return i;
    }
  }
  return -1;
}

function matchingOpen(text, close) {
  let depth = 0;
  for (let i = close; i >= 0; i -= 1) {
    if (text[i] === ')') depth += 1;
    else if (text[i] === '(') {
      depth -= 1;
      if (depth === 0) return i;
    }
  }
  return -1;
}

function isUnarySign(text, index) {
  if (text[index] !== '-' && text[index] !== '+') return false;
  let prev = index - 1;
  while (prev >= 0 && text[prev] === ' ') prev -= 1;
  if (prev < 0) return true;
  return /[(=÷+\-^*/]/.test(text[prev]);
}

function readNumerator(text, slash) {
  let i = slash - 1;
  while (i >= 0 && text[i] === ' ') i -= 1;
  const end = i + 1;
  while (i >= 0) {
    if (text[i] === ')') {
      const open = matchingOpen(text, i);
      if (open < 0) break;
      i = open - 1;
      continue;
    }
    if (isAtomChar(text[i])) {
      i -= 1;
      continue;
    }
    break;
  }
  if (i >= 0 && isUnarySign(text, i)) i -= 1;
  if (i + 1 >= end) return null;
  return { start: i + 1, end };
}

function readDenominator(text, slash) {
  let i = slash + 1;
  while (i < text.length && text[i] === ' ') i += 1;
  const start = i;
  if (i < text.length && isUnarySign(text, i)) i += 1;
  if (i >= text.length) return null;
  if (!isAtomChar(text[i]) && text[i] !== '(') return null;
  while (i < text.length) {
    if (text[i] === '(') {
      const close = matchingClose(text, i);
      if (close < 0) break;
      i = close + 1;
      continue;
    }
    if (!isAtomChar(text[i])) break;
    i += 1;
  }
  if (i <= start) return null;
  return { start, end: i };
}

function looksLikeMath(num, den) {
  const both = `${num}${den}`;
  if (!num || !den) return false;
  if (/[A-Za-z]{2,}/.test(num) && /[A-Za-z]{2,}/.test(den) && !/\d|\^|[()]/.test(both)) return false;
  return /\d|\^|[()]/.test(both);
}

function isDateSlash(text, slash) {
  const window = text.slice(Math.max(0, slash - 6), Math.min(text.length, slash + 8));
  return /\d{1,2}\s*\/\s*\d{1,2}\s*\/\s*\d{2,4}/.test(window);
}

function stripWrap(value) {
  const text = String(value || '').trim();
  if (text[0] !== '(' || text[text.length - 1] !== ')') return text;
  if (matchingClose(text, 0) !== text.length - 1) return text;
  return text.slice(1, -1).trim();
}

function slashInsideExponent(text, slash) {
  let depth = 0;
  for (let i = slash - 1; i >= 0; i -= 1) {
    if (text[i] === ')') depth += 1;
    else if (text[i] === '(') {
      if (depth > 0) {
        depth -= 1;
        continue;
      }
      let prev = i - 1;
      while (prev >= 0 && text[prev] === ' ') prev -= 1;
      if (prev >= 0 && text[prev] === '^') return true;
    }
  }
  return false;
}

function isRadicalAtom(ch) {
  return /[0-9A-Za-z.]/.test(ch) || /[\u0370-\u03FF]/.test(ch);
}

function readRadical(text, at) {
  if (text[at] !== '\u221A') return null;
  let i = at + 1;
  while (i < text.length && text[i] === ' ') i += 1;
  if (i >= text.length) return null;
  if (text[i] === '(') {
    const close = matchingClose(text, i);
    if (close < 0) return null;
    return { end: close + 1, inner: text.slice(i + 1, close) };
  }
  if (!isRadicalAtom(text[i])) return null;
  const start = i;
  while (i < text.length && isRadicalAtom(text[i])) i += 1;
  if (i === start) return null;
  return { end: i, inner: text.slice(start, i) };
}

function findFraction(text, from) {
  for (let slash = text.indexOf('/', from); slash !== -1; slash = text.indexOf('/', slash + 1)) {
    if (text[slash - 1] === '/' || text[slash + 1] === '/') continue;
    if (isDateSlash(text, slash)) continue;
    if (slashInsideExponent(text, slash)) continue;
    const num = readNumerator(text, slash);
    const den = readDenominator(text, slash);
    if (!num || !den || num.start < from) continue;
    const numText = text.slice(num.start, num.end);
    const denText = text.slice(den.start, den.end);
    if (!looksLikeMath(numText, denText)) continue;
    return {
      start: num.start,
      end: den.end,
      num: stripWrap(numText),
      den: stripWrap(denText)
    };
  }
  return null;
}

function isExpAtom(ch) {
  return isAtomChar(ch) && ch !== '^';
}

function isPrimeChar(ch) {
  return ch === "'" || ch === '\u2018' || ch === '\u2019' || ch === '\u2032' || ch === '\u2035';
}

function readExponent(text, hat) {
  if (text[hat] !== '^') return null;
  let i = hat + 1;
  while (i < text.length && text[i] === ' ') i += 1;
  if (i >= text.length) return null;
  if (isPrimeChar(text[i])) return { end: i + 1, inner: '\u2032', paren: false };
  if (text[i] === '(') {
    const close = matchingClose(text, i);
    if (close < 0) return null;
    return { end: close + 1, inner: text.slice(i + 1, close), paren: true };
  }
  const start = i;
  if ((text[i] === '+' || text[i] === '-') && i + 1 < text.length && isExpAtom(text[i + 1])) i += 1;
  const atomStart = i;
  while (i < text.length && isExpAtom(text[i])) i += 1;
  if (i === atomStart) return null;
  return { end: i, inner: text.slice(start, i), paren: false };
}

function exponentNodes(exp) {
  const inner = parseMath(exp.inner);
  if (!exp.paren) return inner;
  if (inner.length === 1 && inner[0].type === 'frac') return inner;
  return [{ type: 'text', text: '(' }, ...inner, { type: 'text', text: ')' }];
}

function findLog(text, from) {
  for (let i = text.indexOf('log', from); i !== -1; i = text.indexOf('log', i + 1)) {
    if (i > 0 && /[A-Za-z]/.test(text[i - 1])) continue;
    if (!/[0-9]/.test(text[i + 3] || '')) continue;
    return { start: i, end: i + 4, base: text[i + 3] };
  }
  return null;
}

function readUnit(text, start) {
  if (text.slice(start, start + 2) !== '[[') return null;
  const close = text.indexOf(']]', start + 2);
  if (close < 0) return null;
  const name = text.slice(start + 2, close);
  if (!name || /[^\w]/.test(name)) return null;
  let end = close + 2;
  const gap = text.slice(end).match(/^\s*\^/);
  if (gap) {
    const exp = readExponent(text, end + gap[0].length - 1);
    if (exp) return { end: exp.end, name, exp };
  }
  return { end, name, exp: null };
}

function parseText(text) {
  const nodes = [];
  let i = 0;
  while (i < text.length) {
    const unitAt = text.indexOf('[[', i);
    const log = findLog(text, i);
    const hat = text.indexOf('^', i);
    const next = Math.min(
      unitAt >= 0 ? unitAt : Infinity,
      log ? log.start : Infinity,
      hat >= 0 ? hat : Infinity
    );
    if (next === Infinity) {
      if (i < text.length) nodes.push({ type: 'text', text: text.slice(i) });
      break;
    }
    if (next > i) nodes.push({ type: 'text', text: text.slice(i, next) });
    if (unitAt >= 0 && next === unitAt) {
      const unit = readUnit(text, unitAt);
      if (!unit) {
        nodes.push({ type: 'text', text: '[[' });
        i = unitAt + 2;
        continue;
      }
      nodes.push({ type: 'text', text: unit.name });
      if (unit.exp) nodes.push({ type: 'sup', body: exponentNodes(unit.exp) });
      i = unit.end;
      continue;
    }
    if (log && next === log.start) {
      nodes.push({ type: 'text', text: 'log' });
      nodes.push({ type: 'sub', body: [{ type: 'text', text: log.base }] });
      i = log.end;
      continue;
    }
    const exp = readExponent(text, hat);
    if (!exp) {
      nodes.push({ type: 'text', text: '^' });
      i = hat + 1;
      continue;
    }
    nodes.push({ type: 'sup', body: exponentNodes(exp) });
    i = exp.end;
  }
  return nodes;
}

function unwrapEquationBrackets(text) {
  let out = '';
  for (let i = 0; i < text.length; i += 1) {
    if (text[i] === '\u3016') {
      const close = text.indexOf('\u3017', i + 1);
      const inner = close > i ? text.slice(i + 1, close) : '';
      const prev = out[out.length - 1];
      if (prev && !/\s/.test(prev) && /^[A-Za-z]/.test(inner)) out += ' ';
      continue;
    }
    if (text[i] === '\u3017') continue;
    out += text[i];
  }
  return out;
}

function splitTop(body, sep) {
  const parts = [];
  let start = 0;
  let paren = 0;
  let bracket = 0;
  for (let i = 0; i < body.length; i += 1) {
    const ch = body[i];
    if (ch === '(') paren += 1;
    else if (ch === ')') paren = Math.max(0, paren - 1);
    else if (ch === '[') bracket += 1;
    else if (ch === ']') bracket = Math.max(0, bracket - 1);
    else if (ch === sep && paren === 0 && bracket === 0) {
      parts.push(body.slice(start, i));
      start = i + 1;
    }
  }
  parts.push(body.slice(start));
  return parts;
}

// Word's linear matrix: [■(x&6@5&15)]. Columns are &, rows are @.
function readWordMatrix(text, at) {
  if (text[at] !== '[') return null;
  let i = at + 1;
  while (i < text.length && text[i] === ' ') i += 1;
  if (text[i] !== '\u25A0') return null;
  i += 1;
  while (i < text.length && text[i] === ' ') i += 1;
  if (text[i] !== '(') return null;
  i += 1;
  const bodyStart = i;
  let depth = 1;
  while (i < text.length && depth > 0) {
    if (text[i] === '(') depth += 1;
    else if (text[i] === ')') depth -= 1;
    if (depth > 0) i += 1;
  }
  if (depth !== 0) return null;
  let end = i + 1;
  while (end < text.length && text[end] === ' ') end += 1;
  if (text[end] !== ']') return null;
  const rawRows = splitTop(text.slice(bodyStart, i), '@').map((row) => (
    splitTop(row, '&').map((cell) => cell.trim())
  ));
  const width = rawRows.reduce((max, row) => Math.max(max, row.length), 0);
  if (!width) return null;
  const rows = rawRows.map((row) => {
    const cells = row.slice();
    while (cells.length < width) cells.push('');
    return cells;
  });
  return { end: end + 1, rows };
}

function findWordMatrix(text, from) {
  for (let i = text.indexOf('[', from); i !== -1; i = text.indexOf('[', i + 1)) {
    const matrix = readWordMatrix(text, i);
    if (matrix) return { start: i, end: matrix.end, rows: matrix.rows };
  }
  return null;
}

/** Leave the surrounding sentence alone and lift out each Word matrix. */
export function splitWordMatrixRuns(text) {
  const source = String(text || '');
  const parts = [];
  let buf = '';
  let i = 0;
  while (i < source.length) {
    const matrix = source[i] === '[' ? readWordMatrix(source, i) : null;
    if (matrix) {
      if (buf) parts.push({ type: 'text', text: buf });
      buf = '';
      parts.push({ type: 'matrix', text: source.slice(i, matrix.end) });
      i = matrix.end;
      continue;
    }
    buf += source[i];
    i += 1;
  }
  if (buf) parts.push({ type: 'text', text: buf });
  if (!parts.length) parts.push({ type: 'text', text: source });
  return parts;
}

/** Hide Word matrices from markdown, which would read [ ... ] as a link. */
export function shieldWordMatrices(text) {
  const source = String(text || '');
  const matrices = [];
  let out = '';
  let i = 0;
  while (i < source.length) {
    const matrix = source[i] === '[' ? readWordMatrix(source, i) : null;
    if (matrix) {
      out += `\uE000m${matrices.length}\uE001`;
      matrices.push(source.slice(i, matrix.end));
      i = matrix.end;
      continue;
    }
    out += source[i];
    i += 1;
  }
  return { text: out, matrices };
}

/** Split a markdown text node back into prose and the matrices it hid. */
export function expandShieldedMatrices(text, matrices) {
  const source = String(text || '');
  const list = Array.isArray(matrices) ? matrices : [];
  const parts = [];
  let last = 0;
  const token = /\uE000m(\d+)\uE001/g;
  let match = token.exec(source);
  while (match) {
    if (match.index > last) parts.push({ type: 'text', text: source.slice(last, match.index) });
    const matrix = list[Number(match[1])];
    parts.push(matrix ? { type: 'matrix', text: matrix } : { type: 'text', text: match[0] });
    last = match.index + match[0].length;
    match = token.exec(source);
  }
  if (last < source.length) parts.push({ type: 'text', text: source.slice(last) });
  if (!parts.length) parts.push({ type: 'text', text: source });
  return parts;
}

function parseMath(source) {
  const text = unwrapEquationBrackets(source);
  const nodes = [];
  let i = 0;
  while (i < text.length) {
    const matrix = findWordMatrix(text, i);
    const frac = findFraction(text, i);
    const radicalAt = text.indexOf('\u221A', i);
    const radical = radicalAt >= 0 ? readRadical(text, radicalAt) : null;
    const matrixAt = matrix ? matrix.start : Infinity;
    const fracAt = frac ? frac.start : Infinity;
    const rootAt = radical ? radicalAt : Infinity;
    if (matrixAt === Infinity && fracAt === Infinity && rootAt === Infinity) {
      if (i < text.length) nodes.push(...parseText(text.slice(i)));
      break;
    }
    if (matrixAt <= fracAt && matrixAt <= rootAt) {
      if (matrixAt > i) nodes.push(...parseText(text.slice(i, matrixAt)));
      nodes.push({
        type: 'matrix',
        rows: matrix.rows.map((row) => row.map((cell) => parseMath(cell)))
      });
      i = matrix.end;
      continue;
    }
    if (rootAt < fracAt) {
      if (rootAt > i) nodes.push(...parseText(text.slice(i, rootAt)));
      nodes.push({ type: 'sqrt', body: parseMath(radical.inner) });
      i = radical.end;
      continue;
    }
    if (frac.start > i) nodes.push(...parseText(text.slice(i, frac.start)));
    nodes.push({
      type: 'frac',
      num: parseMath(frac.num),
      den: parseMath(frac.den)
    });
    i = frac.end;
  }
  return nodes;
}

export function parseFractionNodes(text) {
  return parseMath(String(text || ''));
}

function esc(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const SQRT_SVG = '<svg class="qp-sqrt-svg" viewBox="0 0 14 24" preserveAspectRatio="none" aria-hidden="true"><path d="M1 12 H5 L8 22 L13 1" fill="none" stroke="currentColor" stroke-width="1.6" vector-effect="non-scaling-stroke"></path></svg>';

function nodesToHtml(nodes) {
  return nodes.map((node) => {
    if (node.type === 'text') return esc(node.text);
    if (node.type === 'sup') return `<span class="qp-sup">${nodesToHtml(node.body)}</span>`;
    if (node.type === 'sub') return `<span class="qp-sub">${nodesToHtml(node.body)}</span>`;
    if (node.type === 'sqrt') return `<span class="qp-sqrt">${SQRT_SVG}<span class="qp-sqrt-body">${nodesToHtml(node.body)}</span></span>`;
    if (node.type === 'matrix') {
      const rows = node.rows.map((row) => (
        `<span class="qp-matrix-row">${row.map((cell) => `<span class="qp-matrix-cell">${nodesToHtml(cell)}</span>`).join('')}</span>`
      )).join('');
      return `<span class="qp-matrix"><span class="qp-matrix-bracket" aria-hidden="true"></span><span class="qp-matrix-rows">${rows}</span><span class="qp-matrix-bracket qp-matrix-bracket--right" aria-hidden="true"></span></span>`;
    }
    return `<span class="qp-frac"><span class="qp-frac-num">${nodesToHtml(node.num)}</span><span class="qp-frac-den">${nodesToHtml(node.den)}</span></span>`;
  }).join('');
}

export function fractionHtml(text) {
  return nodesToHtml(parseFractionNodes(text));
}

export const FRACTION_CSS = `
.qp-frac {
  display: inline-flex;
  flex-direction: column;
  vertical-align: middle;
  text-align: center;
  margin: 0 0.15em;
  line-height: 1.05;
  font-size: 0.92em;
}
.qp-frac-num, .qp-frac-den { display: block; padding: 0 0.28em; }
.qp-frac-num { border-bottom: 1.5px solid currentColor; }
.qp-sup, .qp-sub { display: inline-block; font-size: 0.72em; line-height: 1.05; }
.qp-sup { vertical-align: super; }
.qp-sub { vertical-align: sub; }
.qp-sqrt { display: inline-flex; align-items: stretch; vertical-align: middle; margin: 0 0.08em; line-height: 1; }
.qp-sqrt-svg { width: 0.72em; height: auto; align-self: stretch; overflow: visible; flex: 0 0 auto; }
.qp-sqrt-body { border-top: 1.5px solid currentColor; padding: 0.06em 0.2em 0.02em 0.08em; line-height: 1.15; }
.qp-matrix { display: inline-flex; align-items: stretch; vertical-align: middle; margin: 0 0.12em; line-height: 1.2; }
.qp-matrix-bracket { width: 0.32em; border-top: 1.5px solid currentColor; border-bottom: 1.5px solid currentColor; border-left: 1.5px solid currentColor; border-radius: 0.4em 0 0 0.4em; margin: 0.06em 0; }
.qp-matrix-bracket--right { border-left: none; border-right: 1.5px solid currentColor; border-radius: 0 0.4em 0.4em 0; }
.qp-matrix-rows { display: inline-flex; flex-direction: column; justify-content: center; padding: 0.08em 0.06em; }
.qp-matrix-row { display: flex; align-items: center; justify-content: center; }
.qp-matrix-cell { display: flex; align-items: center; justify-content: center; min-width: 0.6em; padding: 0.04em 0.38em; text-align: center; white-space: nowrap; }
`;
